import React, { useContext, useEffect, useState } from 'react';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import { Link, useParams } from 'react-router-dom';
import { AnimatedBackground, ModernCard } from './ui/modern';
import api from '../api';

const OnboardingBranchList = () => {
  const { selected } = useContext(SelectedCompanyContext);
  const { companyId } = useParams();
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!selected || !companyId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get(`/reports/company/${companyId}/preview`);
        if (!cancelled) setPreview(res.data || null);
      } catch (e) {
        console.error('branch list preview fetch', e);
        if (!cancelled) setPreview(null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [companyId, selected]);

  if (!selected) return null;
  const branches = selected.Branches || [];

  const progressByBranchId = {};
  for (const b of preview?.branches || []) {
    progressByBranchId[b.id] = b.percentage;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <AnimatedBackground />

      <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <ModernCard className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-2">
            <span>🏢</span>
            Branches
          </h1>
          <p className="text-slate-400">{selected.name}</p>
        </ModernCard>

        {/* Branches Grid */}
        {branches.length === 0 ? (
          <ModernCard>
            <div className="text-center py-12">
              <div className="text-slate-400 mb-2">No branches found.</div>
              <div className="text-slate-500 text-sm">Add branches to begin onboarding.</div>
            </div>
          </ModernCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map(b => {
              const progress = typeof progressByBranchId[b.id] === 'number' ? progressByBranchId[b.id] : 0;
              const initial = b.name?.charAt(0)?.toUpperCase() || 'B';

              return (
                <Link key={b.id} to={`/onboarding/${companyId}/branches/${b.id}`} className="block group">
                  <ModernCard hover={true} className="h-full">
                    <div className="flex items-start gap-4 mb-4">
                      {/* Branch Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-violet-500 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                        {initial}
                      </div>

                      {/* Branch Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
                          {b.name}
                        </h4>
                        <p className="text-slate-400 text-sm">
                          {b.city || 'No location'}, {b.province || 'N/A'}
                        </p>
                      </div>

                      {/* Arrow indicator */}
                      <div className="text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all">
                        →
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Progress</span>
                        <span className="text-white text-sm font-medium">{progress}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/10">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-400">
                          {b.numberOfUsers || 0}
                        </div>
                        <div className="text-xs text-slate-500">Users</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-violet-400">
                          {b.numberOfDevices || 0}
                        </div>
                        <div className="text-xs text-slate-500">Devices</div>
                      </div>
                    </div>
                  </ModernCard>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingBranchList;
